import moment from "moment";
import dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import i18n from "translations/i18n";
import { Trans } from "react-i18next";
import { COMPANY_CONSTANTS } from "../constants/CompanyConstants";
import { formatPrice, getPaymentStatus } from "./formatters";
import { isLandelijk } from "./region";

export function getCustomerExcelExportData(customers, orders) {
  const getLastOrderDate = (customerId) => {
    if (!orders || orders.length === 0) return "";
    const customerOrders = orders.filter((order) => order.customer?.uid === customerId);
    if (customerOrders.length === 0) return "";
    const lastOrder = customerOrders.reduce((latest, order) => (order.createdAt > latest.createdAt ? order : latest));
    return dayjs.unix(lastOrder.createdAt).format(DATE_FORMAT_DD_MM_YYYY);
  };

  const regionalCustomers = customers.filter((customer) => {
    const zip = customer.address?.delivery?.zipCode;
    return zip && !isLandelijk(zip);
  });

  const nationalCustomers = customers.filter((customer) => {
    const zip = customer.address?.delivery?.zipCode;
    return zip && isLandelijk(zip);
  });

  const columns = [
    { title: "Voornaam" },
    { title: "Achternaam" },
    { title: "E-mailadres" },
    { title: "Laatste bestelling" },
    { title: "Regionaal/Landelijk" },
  ];

  const mapCustomerData = (customer, type) => [
    { value: customer.firstName || "" },
    { value: customer.lastName || "" },
    { value: customer.email || "" },
    { value: getLastOrderDate(customer.id) },
    { value: type },
  ];

  return [
    {
      sheet: "Regionaal",
      columns: columns,
      data: regionalCustomers.length > 0 ? regionalCustomers.map((c) => mapCustomerData(c, "Regionaal")) : [],
    },
    {
      sheet: "Landelijk",
      columns: columns,
      data: nationalCustomers.length > 0 ? nationalCustomers.map((c) => mapCustomerData(c, "Landelijk")) : [],
    },
  ];
}

export function getExcelExportData(rows) {
  return [
    {
      columns: [
        { title: "Bestelnummer" },
        { title: "Besteldatum" },
        { title: "Bedrijfsnaam" },
        { title: "Naam" },
        { title: "Straat" },
        { title: "Huisnr + toev." },
        { title: "Postcode" },
        { title: "Plaats" },
        { title: "Telefoon" },
        { title: "Email" },
        { title: "Status" },
        { title: "Bezorg/afhaaldatum" },
        { title: "Bestelnotities" },
        { title: "Producten" },
        { title: "Totaalprijs" },
      ],
      data:
        rows.length > 0
          ? rows.map((row) => {
              let address = row.customer.address.delivery;
              let products = row.products;
              let productString = "";
              if (!!products) {
                products.forEach((product, index) => {
                  if (index !== products.length - 1) productString += product.quantity + "x " + product.name + "\n";
                  else productString += product.quantity + "x " + product.name;
                });
              }
              return [
                { value: row.orderId },
                { value: moment.unix(row.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm) },
                { value: address.companyName || "" },
                { value: address.firstName + " " + address.lastName },
                { value: address.street },
                { value: address.houseNumber + (address.addition || "") },
                { value: address.zipCode },
                { value: address.city },
                { value: row.customer.phoneNumber },
                { value: row.customer.email },
                { value: getPaymentStatus(row.orderStatus) },
                { value: moment(row.delivery.date, DATE_FORMAT_DD_MM_YYYY).format(DATE_FORMAT_DD_MM_YYYY) },
                { value: row.orderNotes || "-" },
                { value: productString },
                { value: formatPrice(row.totalPrice) },
              ];
            })
          : [],
    },
  ];
}

export function getTrunkrsExportData(rows) {
  return [
    {
      // First row of document has to be skipped
      columns: [{ title: "Bestellingen" }],
      data: [[""]],
    },
    {
      ySteps: -1,
      columns: [
        { title: "Land" },
        { title: "Achternaam" },
        { title: "Voornaam" },
        { title: "Eigen referentie" },
        { title: "Notitie voor de bezorger" },
        { title: "Bedrijfsnaam" },
        { title: "Volledige naam" },
        { title: "Straatnaam" },
        { title: "Huisnummer" },
        { title: "Toevoeging" },
        { title: "Postcode" },
        { title: "Plaats" },
        { title: "Telefoon" },
        { title: "Email" },
        { title: "Pakketsoort" },
        { title: "Buren toegestaan" },
        { title: "Age check" },
        { title: "Bederfbaar" },
        { title: "Maximaal aantal pogingen wanneer bederfbaar" },
        { title: "Afleverdatum drop-off point" },
      ],
      data: rows.map((row, index) => {
        let address = row.customer.address.delivery;
        return [
          { value: "NL" },
          { value: row.customer.lastName },
          { value: row.customer.firstName },
          { value: row.orderId },
          { value: "" },
          { value: address.companyName || "" },
          { value: address.firstName + " " + address.lastName },
          { value: address.street },
          { value: address.houseNumber },
          { value: address.addition || "" },
          { value: address.zipCode },
          { value: address.city },
          { value: row.customer.phoneNumber },
          { value: row.customer.email },
          { value: "sameday_parcel_standard" },
          { value: "WAAR" },
          { value: "ONWAAR" },
          { value: "WAAR" },
          { value: "2" },
          { value: moment().format(DATE_FORMAT_DD_MM_YYYY) },
        ];
      }),
    },
  ];
}

export const productImportSchema = {
  Naam: {
    prop: "name",
    type: String,
  },
  Beschrijving: {
    prop: "description",
    type: String,
    required: true,
  },
  Voorraad: {
    prop: "stock",
    type: Number,
    required: true,
  },
  Min_Aantal: {
    prop: "min_amount",
    type: Number,
    required: true,
  },
  Prijs: {
    prop: "price",
    type: Number,
    required: true,
  },
  BTW_Tarief: {
    prop: "taxRate",
    type: Number,
    required: true,
  },
  Korting: {
    prop: "discount",
    type: Number,
    required: true,
  },
  Categorieën: {
    prop: "category",
    type: [String],
    required: true,
  },
  Type: {
    prop: "type",
    type: [String],
    required: false,
  },
  Variaties: {
    prop: "variations",
    type: {
      Var_Prijs: {
        prop: "price",
        type: [Number],
      },
      Var_Gewicht: {
        prop: "weight",
        type: [String],
      },
      Var_Type: {
        prop: "type",
        type: [String],
      },
    },
  },
};

export const getExcelImportErrorMessage = (error) => {
  return (
    <Trans
      i18nKey="notifications.excel_import_error"
      values={{
        column: error.column,
        row: error.row + 1,
        error: i18n.t(error.error),
      }}
    />
  );
};

export function getPeriodReportExcelExportData(rows) {
  const totalOfRows = rows.reduce((sum, p) => sum + p.totalValue, 0);
  return [
    {
      columns: [
        { title: "Product" },
        { title: "Aantal verkocht" },
        { title: "Totaal omzet" },
        { title: "Gem. prijs" },
        { title: "% van totaal" },
      ],
      data:
        rows.length > 0
          ? rows.map((row) => {
              return [
                { value: row.name },
                { value: row.totalQuantity },
                { value: formatPrice(row.totalValue) },
                { value: formatPrice(row.totalValue / row.totalQuantity) },
                { value: ((row.totalValue / totalOfRows) * 100).toFixed(1) },
              ];
            })
          : [],
    },
  ];
}

export function getChillBillExportData(rows) {
  return [
    {
      columns: [
        { title: "Contact (lossen)" },
        { title: "Bedrijf (lossen)" },
        { title: "Email (lossen)" },
        { title: "Mobiel (lossen)" },
        { title: "Straat (lossen)" },
        { title: "Huisnummer (lossen)" },
        { title: "PostCode (lossen)" },
        { title: "Plaats (lossen)" },
        { title: "Land (lossen)" },
        { title: "Gewenst (lossen)" },
        { title: "Gewenst (lossen, tot)" },
        { title: "Aantal - 1" },
        { title: "Details - 1" },
        { title: "Verpakking" },
        { title: "Notities (lossen)" },
        { title: "Uw kenmerk (lossen)" },
      ],
      data:
        rows.length > 0
          ? rows.map((row) => {
              const address = row.customer.address.delivery;
              const customerName = `${row.customer.firstName} ${row.customer.lastName}`;
              const phoneNumber = row.customer.phoneNumber || "";

              // Calculate total quantity and product details
              let totalQuantity = 0;
              let productDetails = "";
              if (row.products && row.products.length > 0) {
                row.products.forEach((product, index) => {
                  totalQuantity += product.quantity || 0;
                  if (index > 0) productDetails += ", ";
                  productDetails += `${product.quantity}x ${product.name}`;
                });
              }
              const country = !!address.country && address.country === "NL" ? "Nederland" : "België";
              const houseNumber = !!address.houseNumber
                ? address.houseNumber + (address.addition ? " " + address.addition : "")
                : "";

              return [
                { value: customerName },
                { value: address.companyName || "" },
                { value: row.customer.email || "" },
                { value: phoneNumber },
                { value: address.street || "" },
                { value: houseNumber || "" },
                { value: address.zipCode || "" },
                { value: address.city || "" },
                { value: country },
                { value: "" },
                { value: "" },
                { value: "1" },
                { value: "Gekoeld" },
                { value: "Doos" },
                { value: row.orderNotes || "" },
                { value: row.orderId || "" },
              ];
            })
          : [],
    },
  ];
}
